'use client';

import React, { createContext, useContext } from 'react';

import { TooltipContent } from '@repo/ui/components/ui/tooltip';

/**
 * The element the article analysis is laid out in: the modal surface, which
 * is also the size container its `@cred-*` breakpoints query. The analysis
 * was written for the extension's side panel and keyed its styles to the
 * VIEWPORT (`cred-*` media queries), which was only right while it ran in an
 * iframe as wide as that panel. Inline, the panel is the container.
 */
const AnalysisContainerContext = createContext<HTMLElement | null | undefined>(
  undefined,
);

export const AnalysisContainerProvider = AnalysisContainerContext.Provider;

/**
 * The analysis' container element: `null` until the modal surface mounts,
 * `undefined` outside the analysis. Anything that sizes itself in JS reads
 * this rather than the window or `document.body`, which the iframe used to
 * make the panel's own width (see `useResponsiveRadialChartDimensions`).
 */
export const useAnalysisContainer = (): HTMLElement | null | undefined =>
  useContext(AnalysisContainerContext);

/**
 * `TooltipContent` for the analysis. A tooltip portals out of the tree, and
 * outside the container its `@cred-*` classes match nothing; portaling into
 * the container keeps it sized to the panel, like the rest of the analysis,
 * and the container is its collision boundary, so it stays inside the panel
 * the way it stayed inside the iframe.
 */
export const AnalysisTooltipContent = React.forwardRef<
  React.ComponentRef<typeof TooltipContent>,
  Omit<React.ComponentPropsWithoutRef<typeof TooltipContent>, 'container'>
>((props, ref) => {
  const container = useAnalysisContainer() ?? undefined;
  return (
    <TooltipContent
      ref={ref}
      container={container}
      collisionBoundary={container}
      {...props}
    />
  );
});
AnalysisTooltipContent.displayName = 'AnalysisTooltipContent';
